import type { WatchListing, CatalogWatch, SearchResult, ItemDetail } from '../types/watch-monitor'
import { apiGet, apiPost, apiPut } from './api-client'

const USE_MOCK = !import.meta.env.VITE_WATCH_API_URL

// In-memory catalog that persists across fallback calls within the session
let _localCatalog: CatalogWatch[] | null = null

// Tracks whether the watch monitor is using fallback data
let _usingMockData = USE_MOCK
export function isWatchMonitorMock(): boolean { return _usingMockData }

// ─── Mock Data ───────────────────────────────────────────────
const mockListings: WatchListing[] = [
  {
    title: 'Seiko Presage SJE103 Blue Enamel - Full Kit, Excellent Condition',
    price: 2850,
    currency: 'USD',
    url: 'https://www.reddit.com/r/Watchexchange/example1',
    source: 'Reddit r/watchexchange',
    dateListed: '2026-03-01',
    condition: 'Excellent',
    seller: 'u/watchcollector42',
    imageUrl: null,
    referenceNumber: 'SJE103',
    description: 'Full kit with box and papers. Worn less than 10 times. Crystal is flawless. Comes with original bracelet sized for 7" wrist plus extra links.',
    matchedWatch: 'Seiko Presage Urushi SJE103',
    itemId: null,
    listingId: 'mock-1',
    previousPrice: null,
    lastChanged: null,
  },
  {
    title: 'Frederique Constant Highlife Moonphase FC-718NMC4NH6 - BNIB',
    price: 3200,
    currency: 'USD',
    url: 'https://www.chrono24.com/example2',
    source: 'Chrono24',
    dateListed: '2026-03-02',
    condition: 'New',
    seller: 'TrustedSeller_CH',
    imageUrl: null,
    referenceNumber: 'FC-718NMC4NH6',
    description: 'Brand new in box. Full manufacturer warranty. Free shipping worldwide.',
    matchedWatch: 'Frederique Constant Highlife Moonphase FC-718NMC4NH6',
    itemId: null,
    listingId: 'mock-2',
    previousPrice: null,
    lastChanged: null,
  },
  {
    title: 'Monta Skyquest GMT - Blue Dial - Warranty Until 2028',
    price: 1950,
    currency: 'USD',
    url: 'https://www.ebay.com/itm/example3',
    source: 'eBay',
    dateListed: '2026-02-28',
    condition: 'Pre-owned',
    seller: 'premium_watches_store',
    imageUrl: null,
    referenceNumber: 'Skyquest',
    description: 'Beautiful Monta Skyquest GMT in blue. Comes with full kit. Warranty card dated 2024.',
    matchedWatch: 'Monta Skyquest',
    itemId: null,
    listingId: 'mock-3',
    previousPrice: null,
    lastChanged: null,
  },
  {
    title: 'Seiko SPB439 Prospex 1968 Diver Reissue - Like New',
    price: 1100,
    currency: 'USD',
    url: 'https://www.reddit.com/r/Watchexchange/example4',
    source: 'Reddit r/watchexchange',
    dateListed: '2026-03-02',
    condition: 'Like New',
    seller: 'u/diverwatch_fan',
    imageUrl: null,
    referenceNumber: 'SPB439',
    description: 'Purchased December 2025. Worn a handful of times. No marks on case or crystal. Full kit.',
    matchedWatch: 'Seiko Prospex 1968 Diver SPB439',
    itemId: null,
    listingId: 'mock-4',
    previousPrice: null,
    lastChanged: null,
  },
  {
    title: 'AP Quantieme Perpetuel 25657BA Yellow Gold - Rare',
    price: 45000,
    currency: 'USD',
    url: 'https://www.bezel.com/example5',
    source: 'Bezel',
    dateListed: '2026-02-27',
    condition: 'Excellent',
    seller: 'Bezel Authenticated',
    imageUrl: null,
    referenceNumber: '25657BA',
    description: 'Audemars Piguet Quantieme Perpetuel in 18k yellow gold. Recently serviced by AP. Complete with box, papers, and service receipt.',
    matchedWatch: 'Audemars Piguet Quantieme Perpetuel 25657BA',
    itemId: null,
    listingId: 'mock-5',
    previousPrice: null,
    lastChanged: null,
  },
  {
    title: 'Seiko SNR051 Spring Drive Diver - Titanium',
    price: 4800,
    currency: 'USD',
    url: 'https://www.watchbox.com/example6',
    source: 'Watchbox',
    dateListed: '2026-03-01',
    condition: 'Pre-owned',
    seller: 'Watchbox',
    imageUrl: null,
    referenceNumber: 'SNR051',
    description: 'Seiko Prospex Spring Drive Diver. Titanium case. Includes original box and warranty card.',
    matchedWatch: 'Seiko Prospex Spring Drive Diver SNR051',
    itemId: null,
    listingId: 'mock-6',
    previousPrice: null,
    lastChanged: null,
  },
]

const defaultCatalog: CatalogWatch[] = [
  { brand: 'Seiko', model: 'Presage Urushi', reference: 'SJE103', nickname: 'Blue Enamel', search_terms: ['SJE103', 'SJE-103', 'SJE103J1', 'Seiko Presage Urushi', 'Seiko Presage Blue Enamel'], notes: 'Limited Edition Presage with blue enamel dial' },
  { brand: 'Seiko', model: 'Presage Urushi', reference: 'SJE107', nickname: 'Red Enamel', search_terms: ['SJE107', 'SJE-107', 'SJE107J1'], notes: 'Limited Edition Presage with red/burgundy enamel dial' },
  { brand: 'Seiko', model: 'Prospex 1968 Diver', reference: 'SPB439', nickname: '1968 Reissue', search_terms: ['SPB439', 'SPB-439', 'SPB439J1'], notes: 'Prospex 1968 diver reissue' },
  { brand: 'Seiko', model: 'Presage Sharp Edged', reference: 'SPB511', nickname: 'Sharp Edged', search_terms: ['SPB511', 'SPB-511', 'SPB511J1'], notes: 'Presage Sharp Edged series' },
  { brand: 'Monta', model: 'Skyquest', reference: 'Skyquest', nickname: 'GMT', search_terms: ['Monta Skyquest', 'Monta Sky Quest', 'Monta GMT'], notes: "Monta's GMT travel watch" },
  { brand: 'Seiko', model: 'Prospex Spring Drive Diver', reference: 'SNR051', nickname: 'Spring Drive Diver', search_terms: ['SNR051', 'SNR-051', 'SNR051J1'], notes: 'Spring Drive Prospex diver' },
  { brand: 'Seiko', model: 'Prospex Spring Drive Diver', reference: 'SNR055', nickname: 'Spring Drive Diver', search_terms: ['SNR055', 'SNR-055', 'SNR055J1'], notes: 'Spring Drive Prospex diver variant' },
  { brand: 'Jack Mason', model: 'Strat-o-Timer', reference: 'Dark Knight', nickname: 'Dark Knight', search_terms: ['Jack Mason Strat-o-Timer', 'Jack Mason Stratotimer', 'Jack Mason Dark Knight'], notes: 'Dark Knight edition pilot watch' },
  { brand: 'Seiko', model: 'Prospex 1968 Diver', reference: 'SLA083', nickname: 'Professional Diver', search_terms: ['SLA083', 'SLA-083', 'SLA083J1'], notes: 'High-end Prospex professional diver' },
  { brand: 'Kuoe', model: 'Kyoto Royal Smith', reference: '90-006', nickname: 'Royal Smith', search_terms: ['Kuoe 90-006', 'Kuoe Royal Smith', 'Kuoe Kyoto Royal Smith'], notes: 'Japanese microbrand dress watch' },
  { brand: 'Frederique Constant', model: 'Highlife Moonphase', reference: 'FC-718NMC4NH6', nickname: 'Highlife Moonphase', search_terms: ['FC-718NMC4NH6', 'FC718NMC4NH6', 'FC-718'], notes: 'Frederique Constant Highlife with moonphase' },
  { brand: 'Frederique Constant', model: 'Highlife Moonphase', reference: 'FC-718N4NH6B', nickname: 'Highlife Moonphase Black', search_terms: ['FC-718N4NH6B', 'FC718N4NH6B'], notes: 'Highlife Moonphase variant' },
  { brand: 'Frederique Constant', model: 'Highlife Perpetual Calendar', reference: 'FC-775S4NH6', nickname: 'Highlife Perpetual', search_terms: ['FC-775S4NH6', 'FC775S4NH6', 'FC-775'], notes: 'Highlife with perpetual calendar' },
  { brand: 'Frederique Constant', model: 'Highlife Perpetual Calendar', reference: 'FC-775N4NH6B', nickname: 'Highlife Perpetual Black', search_terms: ['FC-775N4NH6B', 'FC775N4NH6B'], notes: 'Highlife Perpetual Calendar variant' },
  { brand: 'Audemars Piguet', model: 'Quantieme Perpetuel', reference: '25657BA', nickname: 'Perpetual Calendar', search_terms: ['25657BA', '25657 BA', 'AP 25657BA', 'Audemars Piguet 25657'], notes: 'Perpetual Calendar in yellow gold' },
]

// ─── API Functions ───────────────────────────────────────────

// Shape returned by the backend GET /api/watch-monitor/listings endpoint
interface BackendListing {
  listingId?: string
  title: string
  price: string
  previousPrice?: string
  source: string
  url: string
  timestamp: string
  lastChanged?: string
  imageUrl?: string
  condition?: string
  seller?: string
  itemId?: string
  matchedWatch?: string
}

function mapBackendListing(b: BackendListing): WatchListing {
  const priceNum = b.price ? Number(b.price) : NaN
  const prevNum = b.previousPrice ? Number(b.previousPrice) : NaN
  return {
    listingId: b.listingId || null,
    title: b.title,
    price: Number.isFinite(priceNum) ? priceNum : null,
    previousPrice: Number.isFinite(prevNum) ? prevNum : null,
    currency: 'USD',
    url: b.url,
    source: b.source,
    dateListed: b.timestamp || null,
    lastChanged: b.lastChanged || null,
    condition: b.condition || null,
    seller: b.seller || null,
    imageUrl: b.imageUrl || null,
    referenceNumber: null,
    description: null,
    // Which catalog watch this listing matched — the backend tags every row it
    // saves, so this is blank only on rows scraped before that existed.
    matchedWatch: b.matchedWatch || '',
    itemId: b.itemId || null,
  }
}

export async function getListings(): Promise<WatchListing[]> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 400))
    return mockListings
  }
  try {
    const result = await apiGet<{ listings: BackendListing[] }>('/api/watch-monitor/listings')
    _usingMockData = false
    return (result.listings || []).map(mapBackendListing)
  } catch {
    console.warn('Watch monitor listings API failed, falling back to mock data')
    _usingMockData = true
    return mockListings
  }
}

// Dismiss (hide) a listing server-side so it stays gone across sessions and
// re-scrapes. No-op in mock mode. Pass dismissed=false to restore.
export async function dismissListing(
  listingId: string,
  dismissed = true,
): Promise<void> {
  if (USE_MOCK || !listingId) return
  await apiPost('/api/watch-monitor/listings/dismiss', { listingId, dismissed })
}

// Fetch full item detail (gallery + description) for the preview modal.
// Only eBay listings have an itemId; returns null when detail is unavailable
// (e.g. mock data, non-eBay sources, or the API failing) so the caller can
// fall back to the data already on the listing.
export async function getItemDetail(itemId: string): Promise<ItemDetail | null> {
  if (USE_MOCK || !itemId) return null
  try {
    return await apiGet<ItemDetail>(
      `/api/watch-monitor/item?id=${encodeURIComponent(itemId)}`,
    )
  } catch {
    console.warn('Watch monitor item-detail API failed')
    return null
  }
}

export async function triggerSearch(): Promise<SearchResult> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 2000))
    return {
      status: 'success',
      totalListings: mockListings.length,
      newListings: mockListings.length,
      listings: mockListings,
      timestamp: new Date().toISOString(),
    }
  }
  try {
    const result = await apiPost<SearchResult>('/api/watch-monitor/search')
    _usingMockData = false
    return result
  } catch {
    console.warn('Watch monitor search API failed, falling back to mock data')
    _usingMockData = true
    return {
      status: 'success',
      totalListings: mockListings.length,
      newListings: mockListings.length,
      listings: mockListings,
      timestamp: new Date().toISOString(),
    }
  }
}

export async function getCatalog(): Promise<CatalogWatch[]> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 500))
    if (!_localCatalog) _localCatalog = [...defaultCatalog]
    return _localCatalog
  }
  try {
    const result = await apiGet<{ watches: CatalogWatch[] }>('/api/watch-monitor/catalog')
    _usingMockData = false
    _localCatalog = result.watches
    return result.watches
  } catch {
    console.warn('Watch monitor catalog API failed, falling back to local data')
    _usingMockData = true
    if (!_localCatalog) _localCatalog = [...defaultCatalog]
    return _localCatalog
  }
}

export async function saveCatalog(watches: CatalogWatch[]): Promise<void> {
  // Always update local copy so edits persist within the session
  _localCatalog = watches

  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 300))
    return
  }
  try {
    await apiPut('/api/watch-monitor/catalog', { watches })
    _usingMockData = false
  } catch {
    console.warn('Watch monitor save API failed, saved locally for this session')
    _usingMockData = true
    // Save succeeded locally — don't throw, so the UI still updates
  }
}
